import { useCallback, useImperativeHandle, useMemo, useState } from 'react';

import { useLanguage } from '../../contexts/language';
import { anything, useField } from '../../hooks/field';
import { useRecordChanges } from '../../hooks/record-changes';
import { useVisit } from '../../hooks/visit';

import { ChoiceList } from '../choice-list';
import { MoodOption } from '../mood-option';
import { StepActions } from '../step-actions';

import styles from './style.module.css';


// Step 1: the mood, one of the sheet's seven named states, elevated at the
// top as on paper (O07: one mark a day). Nothing is chosen until the person
// chooses; an earlier answer for this day is shown chosen.
export function MoodStep({ ref, record, onBack, onSkip, onMove, onSaving, onStopped, onDone }) {
  const { text, translate } = useLanguage();
  const { save } = useRecordChanges();
  const capture = useVisit();
  const [ busy, setBusy ] = useState(false);

  let initial = null;

  if (record.mood != null) {
    initial = String(record.mood);
  }

  const mood = useField({ initial, check: anything });

  const options = useMemo(function() {
    return [ 6, 5, 4, 3, 2, 1, 0 ].map(function(level) {
      return {
        key: String(level),
        label: <MoodOption level={ level } name={ text.moods[ level ].name } anchor={ text.moods[ level ].anchor }/>
      };
    });
  }, [ text ]);

  // Saves what was chosen, then goes to `to`, or to the next step when to is
  // null, as Next does; the bar calls it with the step it chose (#158).
  const submit = useCallback(function(to) {
    if (mood.value === '') {
      return onMove(to);
    }

    setBusy(true);

    const move = onSaving(to);
    const owns = capture();

    return save({ date: record.date, input: { mood: Number(mood.value) } }).then(function() {
      if (owns() === false) {
        return onStopped(move);
      }

      return onDone(move);
    }).catch(function(failed) {
      onStopped(move);

      if (owns() === false) {
        return;
      }

      setBusy(false);
      mood.refuse(failed.message);
    });
  }, [ mood, record, save, onMove, onSaving, onStopped, onDone, capture ]);

  const onSubmit = useCallback(function(event) {
    event.preventDefault();
    submit(null);
  }, [ submit ]);

  useImperativeHandle(ref, function() {
    return { submit };
  }, [ submit ]);

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <ChoiceList
        ref={ mood.ref }
        name='mood'
        legend={ text.moodQuestion }
        options={ options }
        value={ mood.value }
        onChange={ mood.onChange }
        error={ translate(mood.error) }
      />

      <StepActions backLabel={ text.close } onBack={ onBack } submitLabel={ text.next } busy={ busy } skipLabel={ text.skip } onSkip={ onSkip }/>
    </form>
  );
}
